import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { ErrorMessage } from '../../../shared/models/error-message/error-message';
import { map, Observable } from 'rxjs';
import { CreateErrorMessageDto } from '../../../shared/models/error-messages/create-error-message-dto';
import { ErrorMessageQuery } from '../../../shared/models/error-messages/error-message-query';
import { optionalBoolToString } from './api-helpers/optional-bool-to-string';
import { Pagination } from '../../../shared/models/internal/pagination';
import { ErrorMessageWithLinkedDtcs } from '../../../shared/models/error-messages/error-message-with-linked-dtcs';
import { TranslationService } from '../translation.service';
import { CommonUtils, getPropertyName } from '../../common/common-utils';
import { withCache } from '@ngneat/cashew';
import { CloneToSwPartNumbers } from '../../../shared/models/sw-part-number/clone-to-sw-part-numbers';
import { PropertyUpdateInfo } from '../../../services/api/base/property-update-info';
import { PropertyUpdateSynchronisationInfo } from '../../../shared/models/entity-property-info/property-update-synchronisation-info';
import { ErrorMessageUpdate } from '../../../shared/models/error-message/error-message-update';
import { LinkedDtcsUpdateInfo } from '../../../shared/models/error-message/linked-dtcs-update-info';

@Injectable({
  providedIn: 'root',
})
export class ErrorMessageApiService extends PropertyUpdateInfo<ErrorMessage> {
  private commonUtils = CommonUtils;
  private translationService = inject(TranslationService);

  constructor() {
    super(environment.api + 'errormessage');
  }

  public getErrorMessages(
    query: ErrorMessageQuery,
    languageCodeTranslation: string | null = null,
    languageCodeTranslationsState: string | null = null
  ): Observable<Pagination<ErrorMessage>> {
    return this.searchErrorMessages(query, false, languageCodeTranslation, languageCodeTranslationsState);
  }

  public createErrorMessage(createErrorMessage: CreateErrorMessageDto): Observable<ErrorMessage> {
    return this.http.post<ErrorMessage>(this.baseUrl, createErrorMessage);
  }

  public getErrorMessage(id: string, languageCode: string | null = null): Observable<ErrorMessage> {
    if (languageCode == null) {
      languageCode = this.translationService.getLanguage();
    }
    return this.http.get<ErrorMessage>(this.baseUrl + '/' + id + '?languageCode=' + languageCode).pipe(
      map((em) => {
        this.commonUtils.decorateTranslationStateForLanguageCode(em);
        if (em.swPartNumber) {
          em.swPartNumber.displayId = this.commonUtils.getFriendlySwPartNumber(em.swPartNumber);
        }
        em.displayId = em.code?.toString();
        em.extendedDisplayId = `Error Message ${em.displayId}`;
        return em;
      })
    );
  }

  public updateErrorMessage(errorMessageUpdate: ErrorMessageUpdate): Observable<ErrorMessage> {
    return this.http.put<ErrorMessage>(this.baseUrl, errorMessageUpdate);
  }

  public setRemoveFlag(id: string): Observable<void> {
    return this.http.put<void>(this.baseUrl + '/softdelete/' + id, null);
  }

  public getErrorCategories(): Observable<string[]> {
    return this.http.get<string[]>(this.baseUrl + '/errorcategories', {
      context: withCache(),
    });
  }

  public getRecycledErrorMessages(
    query: ErrorMessageQuery,
    languageCodeTranslation: string | null = null,
    languageCodeTranslationsState: string | null = null
  ): Observable<Pagination<ErrorMessage>> {
    return this.searchErrorMessages(query, true, languageCodeTranslation, languageCodeTranslationsState);
  }

  public restoreErrorMessage(id: string): Observable<void> {
    return this.http.put<void>(this.baseUrl + '/restore/' + id, null);
  }

  public deleteErrorMessage(id: string): Observable<void> {
    return this.http.delete<void>(this.baseUrl + '/' + id);
  }

  public updateLinkedDtcs(errorMessageWithLinkedDtcs: ErrorMessageWithLinkedDtcs): Observable<void> {
    return this.http.put<void>(this.baseUrl + '/dtcs', errorMessageWithLinkedDtcs);
  }

  public getLinkedDtcsUpdateInfo(
    errorMessageWithLinkedDtcs: ErrorMessageWithLinkedDtcs
  ): Observable<LinkedDtcsUpdateInfo[]> {
    return this.http.post<LinkedDtcsUpdateInfo[]>(this.baseUrl + '/dtcs/update-info', errorMessageWithLinkedDtcs);
  }

  public searchErrorMessages(
    query: ErrorMessageQuery,
    softDeletedScope: boolean,
    languageCodeTranslation: string | null = null,
    languageCodeTranslationsState: string | null = null
  ): Observable<Pagination<ErrorMessage>> {
    if (languageCodeTranslation == null) {
      // lists are usually displayed in the default language.
      languageCodeTranslation = TranslationService.defaultLanguageCode();
    }
    if (languageCodeTranslationsState == null) {
      languageCodeTranslationsState = languageCodeTranslation;
    }

    const queryParams: Record<string, string> = {
      swPartNumberId: query.swPartNumberId ?? '',
      hasCategoryAlert: optionalBoolToString(query.hasCategoryAlert, true),
      hasCategoryWarning: optionalBoolToString(query.hasCategoryWarning, true),
      hasCategoryInfo: optionalBoolToString(query.hasCategoryInfo, true),
      searchPattern: query.searchPattern ?? '',
      searchInCode: optionalBoolToString(query.searchInCode, true),
      searchInDetailsOnTerminal: optionalBoolToString(query.searchInDetailsOnTerminal, true),
      searchInHeaderOnTerminal: optionalBoolToString(query.searchInHeaderOnTerminal, true),
      searchInOtherFields: optionalBoolToString(query.searchInOtherFields, false),
      ids: query.ids ?? '',
      orderBy: query.orderBy ?? '',
      orderByDescending: query.orderByDescending ? 'true' : 'false',
      languageCode: languageCodeTranslation,
      translationLanguageCodeForSorting: languageCodeTranslationsState,
    };

    if (query.assignedDtcs !== undefined) {
      queryParams[getPropertyName<ErrorMessageQuery>('assignedDtcs')] = query.assignedDtcs.toString();
    }
    if (query.detailsOnTerminalNotEmpty !== undefined) {
      queryParams[getPropertyName<ErrorMessageQuery>('detailsOnTerminalNotEmpty')] =
        query.detailsOnTerminalNotEmpty.toString();
    }
    if (query.mainLanguageTerminalFieldsExportedForReview === true) {
      queryParams[getPropertyName<ErrorMessageQuery>('mainLanguageTerminalFieldsExportedForReview')] =
        query.mainLanguageTerminalFieldsExportedForReview.toString();
    }
    if (query.mainLanguageTerminalFieldsReviewed !== undefined) {
      queryParams[getPropertyName<ErrorMessageQuery>('mainLanguageTerminalFieldsReviewed')] =
        query.mainLanguageTerminalFieldsReviewed.toString();
    }

    this.updateQueryParamsForOther(query, queryParams);

    const url = this.baseUrl + (softDeletedScope ? '/recycled?' : '?');

    return this.http
      .get<ErrorMessage[]>(url + new URLSearchParams(queryParams).toString(), {
        observe: 'response',
      })
      .pipe(
        map((response) => {
          const errorMessages = response.body ?? [];
          const paginationHeader = response.headers.get(this.xPaginationHeader);

          if (paginationHeader) {
            const pagination = JSON.parse(paginationHeader) as Pagination<ErrorMessage>;
            pagination.items = errorMessages.map((em) => {
              this.commonUtils.decorateTranslationStateForLanguageCode(em, languageCodeTranslationsState);
              em.displayId = em.code?.toString();
              em.extendedDisplayId = `Error Message ${em.displayId}`;
              return em;
            });
            return pagination;
          }
          throw new Error('Pagination header not found');
        })
      );
  }

  public cloneErrorMessageToSwPartNumbers(cloneToSwPartNumbers: CloneToSwPartNumbers): Observable<ErrorMessage[]> {
    return this.http.post<ErrorMessage[]>(this.baseUrl + '/clone-to-swpartnumbers', cloneToSwPartNumbers);
  }

  public override getPropertyUpdateInfo(
    pendingErrorMessage: ErrorMessage
  ): Observable<PropertyUpdateSynchronisationInfo> {
    return this.http
      .post<PropertyUpdateSynchronisationInfo>(this.baseUrl + '/property-update-info', pendingErrorMessage)
      .pipe(map((propertyUpdateInfo) => this.mapPropertyUpdateInfo(propertyUpdateInfo)));
  }

  private updateQueryParamsForOther(query: ErrorMessageQuery, queryParams: Record<string, string>): void {
    if (query.page) {
      queryParams[getPropertyName<ErrorMessageQuery>('page')] = query.page.toString();
    }

    if (query.pageSize) {
      queryParams[getPropertyName<ErrorMessageQuery>('pageSize')] = query.pageSize.toString();
    }

    if (query.orderBy) {
      queryParams[getPropertyName<ErrorMessageQuery>('orderBy')] = query.orderBy;
    }

    if (query.orderByDescending) {
      queryParams[getPropertyName<ErrorMessageQuery>('orderByDescending')] = query.orderByDescending ? 'true' : 'false';
    }

    if (query.ids) {
      queryParams[getPropertyName<ErrorMessageQuery>('ids')] = query.ids;
    }

    if (query.translationStatesTerminal && query.translationStatesTerminal.length > 0) {
      queryParams[getPropertyName<ErrorMessageQuery>('translationStatesTerminal')] =
        query.translationStatesTerminal.join(',');
    }

    if (query.changesBy) {
      queryParams[getPropertyName<ErrorMessageQuery>('changesBy')] = query.changesBy;
    }
  }
}
